// src/components/roads/drive/RoadDriveDetectionsPanel.js
// --- UPDATE (Drive Mode AI review) -----------------------------------------------------
// What the tech app's Drive Mode AI found, for one road (roadId) or the whole network:
//   • KPIs — detections, needing review, confirmed, not damage, photos in the training set
//   • damage by type (click to filter)
//   • ROAD: damage per SEGMENT — how many detections and of which type on each ~100 m station,
//     confirmed vs pending, the segment's PCI / health (recalculated from confirmed damage by the
//     existing ML / rules segment evaluation) — click a segment to see only its photos
//     NETWORK: roads with the most detections (click opens the road via onSelectRoad)
//   • photo gallery with the AI boxes drawn on each photo — click to enlarge and confirm /
//     correct / reject (DetectionReviewModal); answers train the detection model
// Live: polls every 60 s while visible and refreshes on road / vision events.
// --- END UPDATE ------------------------------------------------------------------------
import React, { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Flex, HStack, VStack, Text, Badge, Button, Icon, SimpleGrid, Table, Thead, Tbody, Tr, Th, Td,
    Progress, Tooltip, Select, Skeleton, Alert, AlertIcon, AlertDescription, Wrap, WrapItem, IconButton,
    useColorModeValue, useToast, useDisclosure,
} from '@chakra-ui/react';
import { MdCameraAlt, MdRefresh, MdHealthAndSafety, MdFilterAltOff, MdChevronRight } from 'react-icons/md';
import useFlowSenseLive from '../../../hooks/useFlowSenseLive';
import DetectionImage from './DetectionImage';
import DetectionReviewModal from './DetectionReviewModal';
import {
    getDriveOverview, refreshDriveSegmentHealth, damageInfo, verdictInfo, DAMAGE_KEYS,
    pct, fmtWhen, fmtM, pciScheme, pciLabel,
} from './driveAiApi';

const POLL_MS = 60000;

const Kpi = memo(({ label, value, help, color, onClick, active }) => (
    <Box as={onClick ? 'button' : 'div'} type={onClick ? 'button' : undefined} onClick={onClick} textAlign="left" p={2.5} borderRadius="lg"
        borderWidth="1px" borderColor={active ? `${color}.400` : 'transparent'} _hover={onClick ? { borderColor: `${color}.300` } : undefined} aria-pressed={onClick ? !!active : undefined}>
        <Text fontSize="2xs" textTransform="uppercase" fontWeight="700" color="gray.500">{label}</Text>
        <Text fontSize="xl" fontWeight="900" color={`${color}.500`} lineHeight="1.2">{value}</Text>
        {help && <Text fontSize="2xs" color="gray.500" noOfLines={1}>{help}</Text>}
    </Box>
));
Kpi.displayName = 'Kpi';
Kpi.propTypes = { label: PropTypes.string, value: PropTypes.node, help: PropTypes.string, color: PropTypes.string, onClick: PropTypes.func, active: PropTypes.bool };

const RoadDriveDetectionsPanel = ({ roadId = null, roadName, onDataChanged, onSelectRoad, title, embedded = false }) => {
    const toast = useToast();
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [verdict, setVerdict] = useState('');
    const [damageType, setDamageType] = useState('');
    const [segmentIndex, setSegmentIndex] = useState('');
    const [isHealthRunning, setIsHealthRunning] = useState(false);
    const [index, setIndex] = useState(0);
    const viewer = useDisclosure();
    const seqRef = useRef(0);
    const mountedRef = useRef(true);
    useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);
    const changedRef = useRef(onDataChanged); changedRef.current = onDataChanged;

    const cardBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const headBg = useColorModeValue('gray.50', 'gray.900');
    const muted = useColorModeValue('gray.500', 'gray.400');
    const activeRow = useColorModeValue('blue.50', 'blue.900');
    const hoverRow = useColorModeValue('gray.50', 'whiteAlpha.100');
    const tileBg = useColorModeValue('white', 'gray.750');

    const load = useCallback(async (silent = false) => {
        const seq = ++seqRef.current;
        if (!silent) setIsLoading(true);
        try {
            const d = await getDriveOverview({ roadId, verdict: verdict || null, damageType: damageType || null, segmentIndex: segmentIndex === '' ? null : segmentIndex, limit: 90 });
            if (seq !== seqRef.current || !mountedRef.current) return;
            setData(d); setError(null);
        } catch (e) {
            if (seq !== seqRef.current || !mountedRef.current) return;
            setError(e.status === 404 ? 'The Drive Mode AI API is not installed on this server yet (roadRoutes /drive/overview).' : e.message);
        } finally {
            if (seq === seqRef.current && mountedRef.current) setIsLoading(false);
        }
    }, [roadId, verdict, damageType, segmentIndex]);

    useEffect(() => { load(false); }, [load]);
    useEffect(() => { setSegmentIndex(''); setDamageType(''); setVerdict(''); }, [roadId]);
    useEffect(() => {
        const t = setInterval(() => { if (typeof document === 'undefined' || document.visibilityState === 'visible') load(true); }, POLL_MS);
        return () => clearInterval(t);
    }, [load]);
    const liveFilter = useCallback((name, payload) => {
        const p = payload?.payload || payload || {};
        const rid = p.road_id ?? p.roadId ?? null;
        return !roadId || !rid || String(rid) === String(roadId);
    }, [roadId]);
    useFlowSenseLive(['roads'], () => load(true), { debounceMs: 2500, filter: liveFilter, enabled: true });

    const findings = data?.findings || [];
    const totals = data?.totals || {};
    const segments = data?.segments || null;
    const segMax = useMemo(() => Math.max(1, ...(segments || []).map((s) => s.total)), [segments]);

    const openAt = useCallback((i) => { setIndex(i); viewer.onOpen(); }, [viewer]);

    const onReviewed = useCallback((updated) => {
        if (!updated) return;
        setData((d) => (d ? { ...d, findings: d.findings.map((x) => (x.id === updated.id ? updated : x)) } : d));
        // totals / segment counts / PCI change server-side — refresh quietly (health runs ~2 s later)
        setTimeout(() => load(true), 600);
        setTimeout(() => { load(true); try { changedRef.current?.(); } catch { /* best effort */ } }, 4500);
    }, [load]);

    const runHealth = useCallback(async () => {
        if (!roadId) return;
        setIsHealthRunning(true);
        try {
            const r = await refreshDriveSegmentHealth(roadId);
            const ev = r?.evaluation;
            toast({
                title: 'Segment health recalculated',
                description: ev?.error ? `Distress updated; evaluation failed: ${ev.error}`
                    : ev ? `${ev.evaluated} segment(s) scored by ${ev.engine === 'ml' ? 'the ML model' : ev.engine === 'mixed' ? 'ML + rules' : 'FlowSense rules'} · road PCI ${ev.avg_pci ?? '—'}`
                        : r?.note || 'Nothing to update.',
                status: ev?.error ? 'warning' : 'success', duration: 5000,
            });
            await load(true);
            try { changedRef.current?.(); } catch { /* best effort */ }
        } catch (e) {
            toast({ title: 'Recalculation failed', description: e.message, status: 'error', duration: 5000 });
        } finally {
            if (mountedRef.current) setIsHealthRunning(false);
        }
    }, [roadId, toast, load]);

    const filtersOn = verdict || damageType || segmentIndex !== '';
    const clearFilters = () => { setVerdict(''); setDamageType(''); setSegmentIndex(''); };
    const heading = title || (roadId ? `Drive Mode AI detections${roadName ? ` · ${roadName}` : ''}` : 'Drive Mode AI detections · network');

    return (
        <Box bg={embedded ? 'transparent' : cardBg} borderRadius="2xl" borderWidth={embedded ? 0 : '1px'} borderColor={borderColor} p={embedded ? 0 : 4} boxShadow={embedded ? 'none' : 'sm'} aria-label={heading}>
            <Flex justify="space-between" align="center" mb={3} wrap="wrap" gap={2}>
                <HStack spacing={2}>
                    <Icon as={MdCameraAlt} color="purple.500" />
                    <Text fontSize="sm" fontWeight="800">{heading}</Text>
                    {totals.last_at && <Text fontSize="2xs" color={muted}>last {fmtWhen(totals.last_at)}</Text>}
                </HStack>
                <HStack spacing={1}>
                    {roadId && (
                        <Tooltip hasArrow label="Apply confirmed drive damage to each segment and re-score segment PCI with the ML model (rules if it is offline)">
                            <Button size="xs" colorScheme="teal" variant="outline" leftIcon={<Icon as={MdHealthAndSafety} />} onClick={runHealth} isLoading={isHealthRunning} loadingText="Scoring…">
                                Recalculate segment health
                            </Button>
                        </Tooltip>
                    )}
                    <IconButton size="xs" variant="ghost" icon={<Icon as={MdRefresh} />} aria-label="Refresh detections" onClick={() => load(false)} isLoading={isLoading} />
                </HStack>
            </Flex>

            {error && (
                <Alert status="warning" borderRadius="lg" mb={3} py={2}>
                    <AlertIcon /><AlertDescription flex={1} fontSize="xs">{error}</AlertDescription>
                    <Button size="xs" onClick={() => load(false)}>Retry</Button>
                </Alert>
            )}

            {/* KPIs */}
            <SimpleGrid columns={{ base: 2, md: 3, xl: 6 }} spacing={1} mb={3}>
                <Kpi label="Detections" value={isLoading && !data ? '…' : totals.total ?? 0} help={totals.sessions ? `${totals.sessions} drive session(s)` : undefined} color="purple" />
                <Kpi label="Needs review" value={totals.pending ?? 0} help="click to review" color="yellow" active={verdict === 'pending'} onClick={() => setVerdict((v) => (v === 'pending' ? '' : 'pending'))} />
                <Kpi label="Confirmed" value={totals.confirmed ?? 0} help="counted in segment health" color="green" active={verdict === 'confirmed'} onClick={() => setVerdict((v) => (v === 'confirmed' ? '' : 'confirmed'))} />
                <Kpi label="Not damage" value={totals.rejected ?? 0} help="teaches the model" color="gray" active={verdict === 'rejected'} onClick={() => setVerdict((v) => (v === 'rejected' ? '' : 'rejected'))} />
                <Kpi label="In training set" value={totals.in_training ?? 0} help="photos the model learns from" color="blue" />
                <Kpi label={roadId ? 'Off this road' : 'Off the network'} value={roadId ? '—' : totals.off_network ?? 0} help={roadId ? undefined : 'not on a mapped road'} color="orange" />
            </SimpleGrid>

            {/* damage by type */}
            {(data?.by_type || []).length > 0 && (
                <Wrap spacing={1} mb={3}>
                    {data.by_type.map((t) => {
                        const info = damageInfo(t.type);
                        const on = damageType === t.type;
                        return (
                            <WrapItem key={t.type}>
                                <Tooltip hasArrow label={`${t.confirmed} confirmed · ${t.pending} need review · ${t.rejected} not damage`}>
                                    <Button size="xs" variant={on ? 'solid' : 'outline'} colorScheme={info.scheme} onClick={() => setDamageType(on ? '' : t.type)} aria-pressed={on}
                                        leftIcon={<Box w={2} h={2} borderRadius="full" bg={info.color} />}>
                                        {info.label} · {t.total - t.rejected}
                                    </Button>
                                </Tooltip>
                            </WrapItem>
                        );
                    })}
                </Wrap>
            )}

            {/* ROAD: per-segment damage */}
            {segments && (
                <Box mb={3}>
                    <HStack justify="space-between" mb={1}>
                        <Text fontSize="xs" fontWeight="800" textTransform="uppercase" color={muted}>Damage per segment</Text>
                        <Text fontSize="2xs" color={muted}>last {data?.health_window_days ?? '—'} days · click a segment to filter photos</Text>
                    </HStack>
                    {segments.length === 0 ? (
                        <Text fontSize="xs" color={muted} p={3}>This road has no segments yet — draw it on the GIS map to slice it into stations.</Text>
                    ) : (
                        <Box overflow="auto" maxH="340px" borderWidth="1px" borderColor={borderColor} borderRadius="lg">
                            <Table size="sm">
                                <Thead bg={headBg} position="sticky" top={0} zIndex={1}>
                                    <Tr><Th>Segment</Th><Th>Station</Th><Th>Health (PCI)</Th><Th isNumeric>Detections</Th><Th>Damage types</Th><Th>Review</Th><Th>Distress used by ML</Th></Tr>
                                </Thead>
                                <Tbody>
                                    {segments.map((s) => {
                                        const isSel = segmentIndex !== '' && Number(segmentIndex) === s.segment_index;
                                        return (
                                            <Tr key={s.segment_id || `none-${s.segment_index}`} cursor={s.segment_index != null ? 'pointer' : 'default'} bg={isSel ? activeRow : undefined} _hover={{ bg: isSel ? activeRow : hoverRow }}
                                                onClick={() => s.segment_index != null && setSegmentIndex(isSel ? '' : String(s.segment_index))}
                                                aria-selected={isSel}>
                                                <Td fontWeight="800" fontSize="xs">{s.off_segments ? 'No segment' : `#${s.segment_index}`}</Td>
                                                <Td fontSize="xs" whiteSpace="nowrap">{s.station_start_m != null ? `${fmtM(s.station_start_m)} – ${fmtM(s.station_end_m)}` : '—'}</Td>
                                                <Td minW="140px">
                                                    {s.segment_pci != null ? (
                                                        <Tooltip hasArrow label={`${pciLabel(s.segment_pci)}${s.health_updated_at ? ` · drive evidence applied ${fmtWhen(s.health_updated_at)}` : ''}`}>
                                                            <HStack spacing={2}>
                                                                <Progress value={s.segment_pci} size="xs" w="56px" borderRadius="full" colorScheme={pciScheme(s.segment_pci)} aria-label={`PCI ${s.segment_pci}`} />
                                                                <Text fontSize="xs" fontWeight="800">{Math.round(s.segment_pci)}</Text>
                                                                <Badge fontSize="2xs" colorScheme={pciScheme(s.segment_pci)}>{pciLabel(s.segment_pci)}</Badge>
                                                            </HStack>
                                                        </Tooltip>
                                                    ) : <Text fontSize="2xs" color={muted}>—</Text>}
                                                </Td>
                                                <Td isNumeric>
                                                    <HStack spacing={2} justify="flex-end">
                                                        <Box w="48px" h="6px" bg={headBg} borderRadius="full" overflow="hidden" aria-hidden="true">
                                                            <Box h="full" w={`${(s.total / segMax) * 100}%`} bg="purple.400" />
                                                        </Box>
                                                        <Text fontSize="xs" fontWeight="800" minW="18px">{s.total}</Text>
                                                    </HStack>
                                                </Td>
                                                <Td>
                                                    {s.by_type.length === 0 ? <Text fontSize="2xs" color={muted}>none found</Text> : (
                                                        <Wrap spacing={1}>
                                                            {s.by_type.map((t) => (
                                                                <WrapItem key={t.type}>
                                                                    <Badge fontSize="2xs" colorScheme={damageInfo(t.type).scheme} variant={t.confirmed ? 'solid' : 'subtle'}>
                                                                        {damageInfo(t.type).label} {t.total}{t.confirmed && t.confirmed !== t.total ? ` (${t.confirmed}✓)` : ''}
                                                                    </Badge>
                                                                </WrapItem>
                                                            ))}
                                                        </Wrap>
                                                    )}
                                                </Td>
                                                <Td fontSize="2xs" whiteSpace="nowrap">
                                                    {s.confirmed > 0 && <Badge colorScheme="green" fontSize="2xs" mr={1}>{s.confirmed} ✓</Badge>}
                                                    {s.pending > 0 && <Badge colorScheme="yellow" fontSize="2xs" mr={1}>{s.pending} to review</Badge>}
                                                    {s.rejected > 0 && <Badge colorScheme="gray" fontSize="2xs">{s.rejected} ✗</Badge>}
                                                    {!s.confirmed && !s.pending && !s.rejected && <Text as="span" color={muted}>—</Text>}
                                                </Td>
                                                <Td fontSize="2xs" color={muted} whiteSpace="nowrap">
                                                    {s.pothole_count != null ? `${s.pothole_count} pothole(s)` : '—'}{s.cracking_severity && s.cracking_severity !== 'none' ? ` · ${s.cracking_severity} cracking` : ''}
                                                </Td>
                                            </Tr>
                                        );
                                    })}
                                </Tbody>
                            </Table>
                        </Box>
                    )}
                </Box>
            )}

            {/* NETWORK: roads with detections */}
            {!roadId && (data?.roads || []).length > 0 && (
                <Box mb={3}>
                    <Text fontSize="xs" fontWeight="800" textTransform="uppercase" color={muted} mb={1}>Roads with the most detections</Text>
                    <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={1}>
                        {data.roads.slice(0, 9).map((r) => (
                            <HStack key={r.road_id} as={onSelectRoad ? 'button' : 'div'} type={onSelectRoad ? 'button' : undefined} onClick={onSelectRoad ? () => onSelectRoad({ id: r.road_id, name: r.road_name }) : undefined}
                                p={2} borderRadius="md" borderWidth="1px" borderColor={borderColor} _hover={onSelectRoad ? { bg: hoverRow } : undefined} textAlign="left" spacing={2}>
                                <Box flex={1} minW={0}>
                                    <Text fontSize="xs" fontWeight="800" noOfLines={1}>{r.road_name || `#${r.road_id}`}</Text>
                                    <Text fontSize="2xs" color={muted}>{r.total} detection(s) · {r.potholes} pothole(s){r.pending ? ` · ${r.pending} to review` : ''}</Text>
                                </Box>
                                {onSelectRoad && <Icon as={MdChevronRight} color={muted} />}
                            </HStack>
                        ))}
                    </SimpleGrid>
                </Box>
            )}

            {/* filters */}
            <Flex gap={2} mb={2} wrap="wrap" align="center">
                <Select size="xs" maxW="160px" value={verdict} onChange={(e) => setVerdict(e.target.value)} aria-label="Review status filter">
                    <option value="">All review states</option>
                    <option value="pending">Needs review</option>
                    <option value="confirmed">Confirmed / corrected</option>
                    <option value="rejected">Not damage</option>
                </Select>
                <Select size="xs" maxW="180px" value={damageType} onChange={(e) => setDamageType(e.target.value)} aria-label="Damage type filter">
                    <option value="">All damage types</option>
                    {DAMAGE_KEYS.map((k) => <option key={k} value={k}>{damageInfo(k).label}</option>)}
                </Select>
                {segmentIndex !== '' && <Badge colorScheme="blue">Segment {segmentIndex}</Badge>}
                {filtersOn && <Button size="xs" variant="ghost" leftIcon={<Icon as={MdFilterAltOff} />} onClick={clearFilters}>Clear</Button>}
                <Text fontSize="2xs" color={muted} ml="auto">{findings.length} photo(s) · click a photo to enlarge and confirm</Text>
            </Flex>

            {/* gallery */}
            {isLoading && !data ? (
                <SimpleGrid columns={{ base: 2, md: 3, xl: 4 }} spacing={2}>{[0, 1, 2, 3].map((i) => <Skeleton key={i} h="150px" borderRadius="lg" />)}</SimpleGrid>
            ) : findings.length === 0 ? (
                <Text fontSize="xs" color={muted} p={4} textAlign="center">
                    {filtersOn ? 'No photos match these filters.' : roadId ? 'No Drive Mode detections on this road yet. Detections appear here when a technician drives it with Drive Mode.' : 'No Drive Mode detections yet.'}
                </Text>
            ) : (
                <SimpleGrid columns={{ base: 2, md: 3, xl: 4 }} spacing={2}>
                    {findings.map((f, i) => {
                        const dmg = damageInfo(f.damage_type);
                        const vi = verdictInfo(f.verdict);
                        return (
                            <Box key={f.id} bg={tileBg} borderWidth="1px" borderColor={f.verdict === 'reported' ? 'yellow.300' : borderColor} borderRadius="lg" overflow="hidden">
                                <DetectionImage finding={f} onClick={() => openAt(i)} rounded="none" showLabels={false} />
                                <Box p={2}>
                                    <HStack spacing={1} mb={0.5}>
                                        <Box w={2} h={2} borderRadius="full" bg={dmg.color} flexShrink={0} />
                                        <Text fontSize="xs" fontWeight="800" noOfLines={1} flex={1}>{dmg.label}</Text>
                                        <Badge fontSize="9px" colorScheme={vi.scheme}>{vi.label}</Badge>
                                    </HStack>
                                    <Text fontSize="2xs" color={muted} noOfLines={1}>
                                        {!roadId && f.road_name ? `${f.road_name} · ` : ''}{f.segment_index != null ? `Seg ${f.segment_index}` : 'No segment'} · {f.source === 'technician' ? 'technician' : `AI ${pct(f.confidence)}`}
                                    </Text>
                                    <Text fontSize="2xs" color={muted}>{fmtWhen(f.created_at)}</Text>
                                </Box>
                            </Box>
                        );
                    })}
                </SimpleGrid>
            )}

            <DetectionReviewModal isOpen={viewer.isOpen} onClose={viewer.onClose} findings={findings} index={Math.min(index, Math.max(0, findings.length - 1))} onIndexChange={setIndex} onReviewed={onReviewed} />
        </Box>
    );
};

RoadDriveDetectionsPanel.propTypes = {
    roadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    roadName: PropTypes.string,
    onDataChanged: PropTypes.func,
    onSelectRoad: PropTypes.func,
    title: PropTypes.string,
    embedded: PropTypes.bool,
};

export default memo(RoadDriveDetectionsPanel);
